import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import ChildSelector from '../../components/parent/ChildSelector';
import NotePositionChart from '../../components/parent/NotePositionChart';
import { useParentChild } from '../../contexts/ParentChildContext';
import api from '../../services/api';

const ParentNotesPage = () => {
  const { inscriptionId } = useParentChild();
  const [notes, setNotes] = useState([]);
  const [periodes, setPeriodes] = useState([]);
  const [periodeId, setPeriodeId] = useState('');
  const [matiereId, setMatiereId] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!inscriptionId) return;
    api.get(`/parent-portal/children/${inscriptionId}/periodes`)
      .then((res) => { if (res.data.success) setPeriodes(res.data.data); })
      .catch(() => {});
  }, [inscriptionId]);

  useEffect(() => {
    setMatiereId('');
  }, [inscriptionId, periodeId]);

  useEffect(() => {
    if (!inscriptionId) return;
    setLoading(true);
    const params = periodeId ? { periode_id: periodeId } : {};
    api.get(`/parent-portal/children/${inscriptionId}/notes`, { params })
      .then((res) => { if (res.data.success) setNotes(res.data.data); })
      .catch(() => toast.error('Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [inscriptionId, periodeId]);

  const matieres = useMemo(() => {
    const byId = new Map();
    notes.forEach((n) => {
      const id = n.matiere_id ? String(n.matiere_id) : n.lib_matiere;
      if (id && n.lib_matiere) {
        byId.set(id, n.lib_matiere);
      }
    });
    return Array.from(byId.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'fr'));
  }, [notes]);

  const filteredNotes = useMemo(() => {
    if (!matiereId) return notes;
    return notes.filter((n) => {
      const id = n.matiere_id ? String(n.matiere_id) : n.lib_matiere;
      return id === matiereId;
    });
  }, [notes, matiereId]);

  return (
    <div className="space-y-4">
      <ChildSelector />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Résultats</h2>
          <p className="text-sm text-slate-500">Notes publiées avec position dans la classe</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          {periodes.length > 0 && (
            <select
              value={periodeId}
              onChange={(e) => setPeriodeId(e.target.value)}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white"
            >
              <option value="">Toutes les périodes</option>
              {periodes.map((p) => (
                <option key={p.id} value={p.id}>{p.periode}</option>
              ))}
            </select>
          )}
          {matieres.length > 0 && (
            <select
              value={matiereId}
              onChange={(e) => setMatiereId(e.target.value)}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white"
            >
              <option value="">Toutes les matières</option>
              {matieres.map((m) => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full" />
        </div>
      ) : filteredNotes.length === 0 ? (
        <div className="bg-white rounded-xl p-8 text-center text-slate-500">
          {matiereId ? 'Aucune note publiée pour cette matière' : 'Aucune note publiée'}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredNotes.map((n) => (
            <div key={n.eval_id} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium text-slate-900">{n.lib_matiere}</h3>
                  <p className="text-sm text-slate-500">{n.type_evaluation} — {n.periode}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {n.date_evaluation ? new Date(n.date_evaluation).toLocaleDateString('fr-FR') : ''}
                  </p>
                </div>
                <span className="text-lg font-bold text-primary-600">{n.note}</span>
              </div>
              <NotePositionChart note={n.note} noteMin={n.note_min} noteMax={n.note_max} />
              {n.appreciation && (
                <p className="text-xs text-slate-500 mt-2 italic">{n.appreciation}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ParentNotesPage;
